import { writable, derived, Readable, Unsubscriber } from '../libs/store';

const URL = 'wss://api.dabblewriter.com/';
// const URL = 'ws://localhost:6060/';
const BASE_RETRY_TIME = 1000;
const MAX_RETRY_BACKOFF = 3;
const CLOSING_WAIT = 3000;

export interface Connection {
  online: boolean;
  connected: boolean;
  authed: boolean;
  serverTimeOffset: number;
}

export interface Tokens {
  idToken:	string;
  refreshToken:	string;
}


export interface ConnectionStore extends Readable<Connection> {
  connect(): Promise<void>;
  disconnect(): void;
  close(): void;
  send(action: string, ...rest: any[]): Promise<any>;
  sendAfterAuthed(action: string, ...rest: any[]): Promise<any>;
  auth(tokens?: Tokens): Promise<string>;
  pub(channel: string, message: any);
  sub(channel: string, onMessage: Subscriber): Unsubscriber;
}

export type Subscriber = (message: any) => void;

interface Request {
  action: string;
  args: any[];
  resolve(value?: unknown): void;
  reject(reason?: any): void;
}

export const connection = createConnectionStore();

export function createConnectionStore(): ConnectionStore {
  const requests: {[r: string]: Request} = {};
  const afterAuthedQueue: Array<[string, any[], (value?: unknown) => void, (reason?: any) => void]> = [];

  let socket: WebSocket;
  let shouldConnect = false;
  let requestNumber = 1;
  let online = window.navigator.onLine;
  let connected = false;
  let authed: boolean = false;
  let serverTimeOffset = 0;
  let retries = 0;
  let reconnectTimeout: any;
  let closing: any;
  let subs: Record<number, Subscriber> = {};

  const { set: setData, get, subscribe } = writable<Connection>({ online, connected, authed, serverTimeOffset });

  function sub(channel: string, onMessage: Subscriber) {
    let unsub = false;
    let ref = 0;

    send('subscribe', channel).then((value: number) => {
      ref = value;
      if (unsub) unsubscribe();
      else subs[ref] = onMessage;
    });

    function unsubscribe() {
      unsub = true;
      delete subs[ref];
      if (ref) send('unsubscribe', ref);
    }

    return unsubscribe;
  }

  function pub(channel: string, message: any) {
    send('publish', channel, message);
  }

  window.addEventListener('online', onOnline);
  window.addEventListener('offline', onOffline);

  function close() {
    window.removeEventListener('online', onOnline);
    window.removeEventListener('offline', onOffline);
  }

  function update() {
    setData({ online, connected, authed, serverTimeOffset });
  }

  function connect(): Promise<void> {
    clearTimeout(reconnectTimeout);

    return new Promise((resolve, reject) => {
      shouldConnect = true;

      if (!online) {
        return reject(new Error('offline'));
      } else if (connected) {
        return;
      }

      try {
        socket = new WebSocket(URL);
      } catch (err) {
        reject(err);
      }

      socket.onerror = () => {
        reject();
        closeSocket();
      };

      socket.onopen = async () => {
        if (!connected) {
          connected = true;
          update();
        }
        retries = 0;
        resolve();
      };

      socket.onclose = () => {
        clearTimeout(closing);
        closing = null;
        socket.onclose = null;
        socket = null;
        if (connected) {
          connected = false;
          authed = null;
          update();
        }

        Object.keys(requests).forEach(key => {
          const request = requests[key];
          request.reject(new Error('CONNECTION_CLOSED'));
          delete requests[key];
        });

        if (shouldConnect && online) {
          const backoff = Math.round((Math.random() * (Math.pow(2, retries) - 1)) * BASE_RETRY_TIME);
          retries = Math.min(MAX_RETRY_BACKOFF, retries + 1);
          reconnectTimeout = setTimeout(() => {
            connect().catch(err => {});
          }, backoff);
        }
      };

      socket.onmessage = event => {
        let data;
        try {
          data = JSON.parse(event.data);
        } catch (err) {
          console.error('Unparseable data from socket:', event.data);
          return;
        }

        if (data.ts) {
          serverTimeOffset = data.ts - Date.now();
          update();
          return;
        }

        if (data.p) {
          if (data.d && data.d._connection) {
            if (data.d._connection === 'closing' && connected) {
              connected = false;
              authed = null;
              update();
              if (!Object.keys(requests).length) {
                closeSocket();
              } else {
                closing = setTimeout(closeSocket, CLOSING_WAIT);
              }
            }
          } else {
            const { p: ref, d: message } = data;
            const onMessage = subs[ref];
            if (onMessage) onMessage(message);
          }
          return;
        }

        const request = requests[data.r];
        if (!request) return; // for now
        if (data.err) {
          console.log('Error with send', request.action, request.args);
          request.reject(new Error(data.err));
        } else {
          request.resolve(data.d);
        }
      };
    })
  }

  function disconnect() {
    shouldConnect = false;
    closeSocket();
  }

  function closeSocket() {
    if (!socket) return;
    connected = false;
    authed = null;
    update();
    socket.close();
    if (socket) (socket.onclose as any)();
  }

  function send(action: string, ...args: any[]): Promise<any> {
    if (!socket || socket.readyState !== 1 || closing) {
      return Promise.reject(new Error('DISCONNECTED'));
    }

    while (args.length && args[args.length - 1] === undefined) args.pop();

    const r = requestNumber++;
    return new Promise((resolve, reject) => {
      requests[r] = { action, args, resolve, reject };
      try {
        socket.send(JSON.stringify({ r, a: action, d: args.length ? args : undefined }));
      } catch (err) {
        console.error('Exception thrown from WebSocket.send():', err.message, 'Closing connection.');
      }
    }).finally(() => {
      delete requests[r];

      if (closing && !Object.keys(requests).length && socket) {
        closeSocket();
      }
    });
  }

  function sendAfterAuthed(a: string, ...rest: any[]): Promise<any> {
    if (authed) return send(a, ...rest);

    return new Promise((resolve, reject) => {
      afterAuthedQueue.push([ a, rest, resolve, reject ]);
    });
  }

  async function auth(tokens?: Tokens) {
    const uid = await send('auth', tokens);
    authed = !!uid;
    update();
    while (afterAuthedQueue.length) {
      const [ a, rest, resolve, reject ] = afterAuthedQueue.shift();
      send(a, ...rest).then(resolve, reject);
    }
    return uid;
  }

  function onOnline() {
    online = true;
    update();
    if (shouldConnect) {
      connect().catch(err => {});
    }
  }

  function onOffline() {
    online = false;
    update();
    closeSocket();
  }


  return {
    close,
    connect,
    disconnect,
    send,
    sendAfterAuthed,
    auth,
    sub,
    pub,
    get,
    subscribe,
  };
}
